import { useEffect, useState } from "react";
import {
  fetchMockInstitutions,
  connectMockInstitution,
  removeMockInstitution,
} from "../../../../../api/finances/plaidConnectivityAPI";
import InstitutionItem from "./InstitutionItem";
import GhostBar from "../../../loading/skeletons/GhostBar";
import FiInsightCard from "../../../../../pages/private/financial-insights/common/FiInsightCard";

function TestAccountInstitutionBody({
  institutions = [],
  isLoading = false,
  focusedInstitutionUuid,
  onInstitutionConnected,
  onInstitutionRemoved,
  onSetError,
  copy,
  connectActionTrigger,
  setIsConnectDisabled,
}) {
  const [isSelectingMock, setIsSelectingMock] = useState(false);
  const [availableMocks, setAvailableMocks] = useState([]);
  const [selectedMockUuid, setSelectedMockUuid] = useState("");
  const [isLoadingMocks, setIsLoadingMocks] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync header button state
  useEffect(() => {
    setIsConnectDisabled(isSelectingMock || isSubmitting);
  }, [isSelectingMock, isSubmitting, setIsConnectDisabled]);

  // Handle header button click trigger
  useEffect(() => {
    if (connectActionTrigger > 0) {
      setIsSelectingMock(true);
    }
  }, [connectActionTrigger]);

  // Fetch available template mock institutions whenever selection view opens
  useEffect(() => {
    if (!isSelectingMock) return;

    const loadAvailableMocks = async () => {
      setIsLoadingMocks(true);
      onSetError("");
      try {
        const data = await fetchMockInstitutions();

        const mockList = Array.isArray(data)
          ? data
          : Array.isArray(data?.mock_institutions)
          ? data.mock_institutions
          : Array.isArray(data?.institutions)
          ? data.institutions
          : [];

        setAvailableMocks(mockList);

        if (mockList.length > 0) {
          setSelectedMockUuid(mockList[0].uuid || mockList[0].id);
        }
      } catch (error) {
        console.error("Error fetching mock options:", error);
        setAvailableMocks([]);
        onSetError("Failed to load available mock institutions.");
      } finally {
        setIsLoadingMocks(false);
      }
    };

    loadAvailableMocks();
  }, [isSelectingMock, onSetError]);

  // Handle mock connection submission
  const handleConfirmMockConnect = async () => {
    if (!selectedMockUuid) return;

    setIsSubmitting(true);
    onSetError("");

    try {
      const result = await connectMockInstitution(selectedMockUuid);

      onInstitutionConnected?.(result);
      setIsSelectingMock(false);
    } catch (error) {
      console.error("Error connecting mock institution:", error);
      onSetError("Failed to connect mock institution.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle mock institution removal / un-swap
  const handleRemoveMockInstitution = async (institutionToRemove) => {
    const instUuid = institutionToRemove?.uuid;
    if (!instUuid) return;

    await removeMockInstitution(instUuid);
    onInstitutionRemoved?.(institutionToRemove);
  };

  return (
    <>
      {isSelectingMock ? (
        <div
          className="audit-protection-content"
          style={{ display: "flex", flexDirection: "column", gap: "16px" }}
        >
          <div>
            <p style={{ margin: "0 0 4px 0", fontWeight: "500" }}>
              Select a Mock Institution
            </p>
            <span
              className="light-description"
              style={{ fontSize: "13px", color: "#6b7280" }}
            >
              Select a test bank to instantly populate simulated financial
              accounts and transaction data.
            </span>
          </div>

          {isLoadingMocks ? (
            <GhostBar />
          ) : !Array.isArray(availableMocks) || availableMocks.length === 0 ? (
            <div
              style={{
                padding: "12px 0",
                color: "#6b7280",
                fontSize: "14px",
              }}
            >
              No mock institutions available to connect.
            </div>
          ) : (
            availableMocks.map((mockInst) => {
              const targetKey = mockInst.uuid || mockInst.id;
              return (
                <div
                  key={targetKey}
                  className="audit-protection-checkbox-container"
                  onClick={() => setSelectedMockUuid(targetKey)}
                  style={{ cursor: "pointer", marginBottom: 0 }}
                >
                  <label
                    className="audit-protection-checkbox-label"
                    style={{
                      pointerEvents: "none",
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    <div className="audit-protection-checkbox-wrapper">
                      <input
                        type="checkbox"
                        checked={selectedMockUuid === targetKey}
                        readOnly
                        className="audit-protection-checkbox"
                      />
                    </div>
                    <span>{mockInst.institution_name || mockInst.name}</span>
                  </label>
                </div>
              );
            })
          )}

          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "12px",
              borderTop: "1px solid #E5E7EB",
              paddingTop: "16px",
              marginTop: "8px",
            }}
          >
            <button
              type="button"
              onClick={() => setIsSelectingMock(false)}
              className="cta-button secondary"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleConfirmMockConnect}
              className="cta-button primary"
              disabled={isSubmitting || isLoadingMocks || !selectedMockUuid}
            >
              {isSubmitting ? "Connecting..." : "Connect Mock Institution"}
            </button>
          </div>
        </div>
      ) : (
        <>
          {isLoading ? (
            <GhostBar />
          ) : (
            institutions.map((inst, index) => (
              <InstitutionItem
                key={inst.uuid || index}
                institution={inst}
                defaultOpen={
                  focusedInstitutionUuid
                    ? inst.uuid === focusedInstitutionUuid
                    : index === 0
                }
                isTestAccount={true}
                onRemoveInstitution={handleRemoveMockInstitution}
                onInstitutionRemoved={onInstitutionRemoved}
                onInstitutionUpdated={(updatedInstitution) =>
                  onInstitutionConnected?.({ institution: updatedInstitution })
                }
              />
            ))
          )}

          {!isLoading && institutions.length === 0 && (
            <FiInsightCard title={copy?.empty_title}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "16px",
                  marginTop: "8px",
                }}
              >
                <p
                  className="reset-margin"
                  style={{
                    color: "#6b7280",
                    fontSize: "14px",
                    lineHeight: "1.5",
                    margin: 0,
                  }}
                >
                  {copy?.empty_description}
                </p>
              </div>
            </FiInsightCard>
          )}
        </>
      )}
    </>
  );
}

export default TestAccountInstitutionBody;
